import { useState, useCallback } from 'react';
import { toPng, toCanvas } from 'html-to-image';
import { validateShareLink, validateExportPdf, validateExportGif } from '@/src/infrastructure/lib/api';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface UseScreenshotShareOptions {
  /** The element shown in the live preview. Used for static PNG / WhatsApp share. */
  elementRef: React.RefObject<HTMLElement | null>;
  /**
   * Optional separate ref used ONLY for GIF capture.
   * Point this at an off-screen, unconstrained container that renders the full
   * template at its natural height — free of any parent height/overflow clipping.
   * Falls back to elementRef when not provided.
   */
  captureRef?: React.RefObject<HTMLElement | null>;
  fileName?: string;
  width?: number;
  height?: number;
  quality?: number;
  pixelRatio?: number;
  invitationId?: string;
  /** Public slug used by downloadPdf to build the /i/:slug?print=true URL. */
  publicSlug?: string;
}

export interface GifProgress {
  current: number;
  total: number;
  estimatedSecsRemaining?: number;
  templateType?: 'heavy' | 'moderate' | 'static';
}

// Detect animation complexity in the template
function detectAnimationComplexity(element: HTMLElement): {
  hasAnimations: boolean;
  type: 'static' | 'moderate' | 'heavy';
  fps: number;
  durationMs: number;
  frameCount: number;
} {
  const canvases = element.querySelectorAll('canvas');
  const animatedElements = element.querySelectorAll('[class*="animate"], [class*="transition"], [class*="keyframe"]');
  const hasKeyframes = window.getComputedStyle(element).animationDuration !== 'auto';

  // Heavy: Canvas animations (Lagna Patrika petals, particle effects)
  if (canvases.length > 0) {
    return {
      hasAnimations: true,
      type: 'heavy',
      fps: 15,
      durationMs: 4000,
      frameCount: 60,
    };
  }

  // Moderate: CSS animations/transitions
  if (animatedElements.length > 3 || hasKeyframes) {
    return {
      hasAnimations: true,
      type: 'moderate',
      fps: 10,
      durationMs: 2000,
      frameCount: 20,
    };
  }

  // Static: No detected animations
  return {
    hasAnimations: false,
    type: 'static',
    fps: 4,
    durationMs: 500,
    frameCount: 2,
  };
}

// ---------------------------------------------------------------------------
// GIF scroll-pan timing
// ---------------------------------------------------------------------------
// Frame count is calculated dynamically so each frame advances ~PIXELS_PER_FRAME
// pixels regardless of template height. Every section is shown long enough to read.
//
// FIRST_FRAME_DELAY_MS  — hold the opening shot so the viewer orients
// MID_FRAME_DELAY_MS    — each scrolling frame (~15 fps for smooth motion)
// LAST_FRAME_DELAY_MS   — hold the closing shot ("landing" pause)
const PIXELS_PER_FRAME     = 80;   // px advanced per frame (larger = fewer frames, faster pan)
const MAX_FRAMES           = 80;   // safety cap
const FIRST_FRAME_DELAY_MS = 1200; // ms to hold opening frame
const MID_FRAME_DELAY_MS   = 66;   // ms per scrolling frame (~15 fps)
const LAST_FRAME_DELAY_MS  = 1500; // ms to hold closing frame

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useScreenshotShare({
  elementRef,
  captureRef,
  fileName = 'invitation.png',
  width = 1200,
  height = 630,
  quality = 1.0,
  pixelRatio = 2,
  invitationId,
  publicSlug,
}: UseScreenshotShareOptions) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingGif, setIsGeneratingGif] = useState(false);
  const [gifProgress, setGifProgress] = useState<GifProgress | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // -------------------------------------------------------------------------
  // generateImage — static PNG from the visible preview element
  // -------------------------------------------------------------------------
  const generateImage = useCallback(async (): Promise<string | null> => {
    if (!elementRef.current) {
      setError(new Error('Element reference is not set.'));
      return null;
    }

    setIsGenerating(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const dataUrl = await toPng(elementRef.current, {
        quality,
        pixelRatio,
        cacheBust: true,
        width,
        height,
        style: { transform: 'scale(1)', transformOrigin: 'top left' },
      });
      return dataUrl;
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to generate image');
      setError(e);
      console.error('Image generation failed:', e);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [elementRef, width, height, quality, pixelRatio]);

  // -------------------------------------------------------------------------
  // downloadImage
  // -------------------------------------------------------------------------
  const downloadImage = useCallback(async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return false;

    try {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      link.click();
      return true;
    } catch (err) {
      console.error('Download failed:', err);
      return false;
    }
  }, [generateImage, fileName]);

  // -------------------------------------------------------------------------
  // shareToWhatsApp
  //
  // Opens the WhatsApp share URL directly with pre-filled text (message + link).
  // WhatsApp's link preview fetches OG tags from the hosted URL and renders a
  // rich card (image + title + description) — no file attachment needed.
  //
  // Why bypass navigator.share with a file:
  //   On iOS/Android, `navigator.share({ files: [pngBlob] })` opens the native
  //   share sheet which does NOT pre-select WhatsApp and drops the text URL.
  //   Sharing directly via the WhatsApp API URL is more reliable cross-platform
  //   and lets the OG image render as the link preview instead of an attachment.
  // -------------------------------------------------------------------------
  const shareToWhatsApp = useCallback(
    async (title: string, text: string) => {
      if (invitationId) {
        try {
          await validateShareLink(invitationId);
        } catch (err) {
          const e = err instanceof Error ? err : new Error('Unauthorized share action');
          setError(e);
          return false;
        }
      }

      // wa.me is the modern, canonical share URL (works on web, iOS, Android)
      const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(waUrl, '_blank');
      return true;
    },
    [invitationId],
  );

  // -------------------------------------------------------------------------
  // generateGif
  //
  // Strategy:
  //  1. Prefer `captureRef` (an off-screen, unconstrained container) over
  //     `elementRef` which is clipped to 630 px by the preview layout.
  //  2. Capture ONE full-height canvas of that container.
  //  3. Crop 1200×630 slices at linearly spaced Y offsets → scroll-pan frames.
  //  4. Encode into a GIF via gif.js.
  //
  // Why not use `elementRef` directly?
  //   The preview container has `height: 630px` imposed by an ancestor inline
  //   style. Setting height:auto on the child doesn't help because the parent
  //   remains 630 px, so scrollHeight === 630 and maxScroll === 0 → every
  //   frame is identical.  The off-screen capture container has no such
  //   constraint, so it renders the template at its natural full height.
  // -------------------------------------------------------------------------
  const generateGif = useCallback(
    async (): Promise<Blob | null> => {
      const captureEl = (captureRef ?? elementRef).current;
      if (!captureEl) {
        setError(new Error('Capture element is not mounted.'));
        return null;
      }

      const animationInfo = detectAnimationComplexity(captureEl);
      const templateType = animationInfo.type;

      setIsGeneratingGif(true);
      // Set a rough initial total; we'll refine once we know scroll height
      setGifProgress({ current: 0, total: MAX_FRAMES, estimatedSecsRemaining: 30, templateType });
      setError(null);

      try {
        // ── Step 1: wait for the off-screen element to fully render ────────────
        // Two rAF ticks ensure the browser has painted at least once.
        await new Promise((r) => requestAnimationFrame(r));
        await new Promise((r) => requestAnimationFrame(r));
        await new Promise((r) => setTimeout(r, 800));

        // ── Step 2: measure the true full content height ─────────────────────
        const fullContentHeight = captureEl.scrollHeight;
        const maxScroll = Math.max(0, fullContentHeight - height);
        console.log('[GIF] fullContentHeight:', fullContentHeight, 'maxScroll:', maxScroll);

        // ── Step 3: calculate frame count from actual scroll distance ─────────
        // Each frame advances PIXELS_PER_FRAME px so every section is shown.
        // Minimum 2 frames (first + last hold); cap at MAX_FRAMES.
        const scrollFrames: number = maxScroll > 0
          ? Math.min(MAX_FRAMES - 2, Math.ceil(maxScroll / PIXELS_PER_FRAME))
          : 0;
        // Total frames: first-hold + scrolling frames + last-hold
        // (when no scroll, we just produce 2 frames: open + close)
        const totalFrames: number = scrollFrames + 2;
        const totalDurationMs =
          FIRST_FRAME_DELAY_MS +
          scrollFrames * MID_FRAME_DELAY_MS +
          LAST_FRAME_DELAY_MS;

        console.log('[GIF] totalFrames:', totalFrames, '| est duration:', Math.round(totalDurationMs / 1000), 's');

        // Update progress with accurate total
        setGifProgress({
          current: 0,
          total: totalFrames,
          estimatedSecsRemaining: Math.round(totalDurationMs / 1000),
          templateType,
        });

        // ── Step 4: capture one full-height canvas of the entire template ────
        const fullCanvas = await toCanvas(captureEl, {
          width,
          height: fullContentHeight,
          pixelRatio: 1,
          cacheBust: true,
          skipFonts: false,
        });

        // ── Step 5: set up GIF encoder ─────────────────────────────────
        // Output at 800×420 to keep file size sensible for WhatsApp.
        const gifW = Math.round(width / 1.5);   // 800
        const gifH = Math.round(height / 1.5);  // 420

        const GIF = (await import('gif.js')).default;
        const encoder = new GIF({
          workers: 4,
          quality: 8,
          width: gifW,
          height: gifH,
          workerScript: '/gif.worker.js',
          background: '#ffffff',
          repeat: 0, // loop forever
        });

        // Shared offscreen canvas — reused for every frame
        const frameCanvas = document.createElement('canvas');
        frameCanvas.width = gifW;
        frameCanvas.height = gifH;
        const ctx = frameCanvas.getContext('2d')!;

        /** Render srcY offset into frameCanvas and push to encoder. */
        const addFrame = (srcY: number, delayMs: number) => {
          ctx.clearRect(0, 0, gifW, gifH);
          ctx.drawImage(
            fullCanvas,
            0,     srcY,   // source origin
            width, height, // source size  (1200 × 630 slice)
            0,     0,      // dest origin
            gifW,  gifH,   // dest size    (800 × 420)
          );
          encoder.addFrame(frameCanvas, { delay: delayMs, copy: true });
        };

        // ── Step 6: build frames ──────────────────────────────────────────
        let framesDone = 0;

        const updateProgress = (secsLeft: number) => {
          framesDone++;
          setGifProgress({ current: framesDone, total: totalFrames, estimatedSecsRemaining: secsLeft, templateType });
        };

        // Frame 0 — opening hold (top of invitation)
        addFrame(0, FIRST_FRAME_DELAY_MS);
        updateProgress(Math.round((totalDurationMs - FIRST_FRAME_DELAY_MS) / 1000));
        await new Promise((r) => setTimeout(r, 0));

        // Scrolling frames
        for (let i = 1; i <= scrollFrames; i++) {
          const progress = scrollFrames <= 1 ? 1 : i / scrollFrames;
          const srcY = Math.round(progress * maxScroll);
          const secsLeft = Math.round(
            ((scrollFrames - i) * MID_FRAME_DELAY_MS + LAST_FRAME_DELAY_MS) / 1000,
          );
          addFrame(srcY, MID_FRAME_DELAY_MS);
          updateProgress(secsLeft);
          // Yield so the browser can repaint the progress bar
          await new Promise((r) => setTimeout(r, 0));
        }

        // Last frame — closing hold (bottom of invitation)
        addFrame(maxScroll, LAST_FRAME_DELAY_MS);
        updateProgress(0);
        await new Promise((r) => setTimeout(r, 0));

        // ── Step 7: render the GIF ───────────────────────────────────────────
        return await new Promise<Blob | null>((resolve) => {
          encoder.on('finished', (blob: Blob) => resolve(blob));
          encoder.on('abort', () => resolve(null));
          encoder.render();
        });
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to generate GIF');
        setError(e);
        console.error('GIF generation failed:', e);
        return null;
      } finally {
        setIsGeneratingGif(false);
        setGifProgress(null);
      }
    },
    [captureRef, elementRef, width, height],
  );

  // -------------------------------------------------------------------------
  // downloadPdf
  //
  // Strategy: open the live public invitation URL in a new tab with ?print=true.
  // The PublicInvitation page detects this flag, applies print CSS (hides nav/
  // sharing elements), and auto-calls window.print() + window.close().
  //
  // This guarantees 100% visual fidelity — the same React render path used by
  // the public page, with real fonts, images, and animations — and avoids the
  // blank-page/clipping problems of canvas screenshot approaches.
  // -------------------------------------------------------------------------
  const downloadPdf = useCallback(async (): Promise<boolean> => {
    if (invitationId) {
      try {
        await validateExportPdf(invitationId);
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Unauthorized export action');
        setError(e);
        return false;
      }
    }

    // Open the live public invitation page with ?print=true. The page detects
    // this flag and auto-calls window.print() + window.close().
    const slug = publicSlug ?? fileName.replace(/\.png$/i, '').replace(/^invitation-/, '');
    const printUrl = `${window.location.origin}/i/${encodeURIComponent(slug)}?print=true`;
    window.open(printUrl, '_blank');
    return true;
  }, [publicSlug, fileName, invitationId]);

  // -------------------------------------------------------------------------
  // downloadGif
  // -------------------------------------------------------------------------
  const downloadGif = useCallback(
    async () => {
      if (invitationId) {
        try {
          await validateExportGif(invitationId);
        } catch (err) {
          const e = err instanceof Error ? err : new Error('Unauthorized export action');
          setError(e);
          return false;
        }
      }

      const blob = await generateGif();
      if (!blob) return false;

      try {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = fileName.replace(/\.png$/i, '.gif');
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return true;
      } catch (err) {
        console.error('GIF download failed:', err);
        return false;
      }
    },
    [generateGif, fileName, invitationId],
  );

  return {
    isGenerating,
    isGeneratingGif,
    gifProgress,
    error,
    generateImage,
    downloadImage,
    downloadPdf,
    shareToWhatsApp,
    generateGif,
    downloadGif,
  };
}
